import { TooltipV2 } from '@cimplico/components';
import { Link } from 'react-router-dom';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useClientQueries } from '../../queries/use-client-queries';
import { ClientQueryOverview, ClientQueryStatusEnum } from '../../types/client-query';
import { calcRelativeTime, formatDateTime } from '../../utilities/formatting';
import { getQueryContext } from '../client-queries/utils';
import { InboxIcon } from '@heroicons/react/24/outline';
import UserAvatar from '../../components/avatar/UserAvatar';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import SummaryTableTitle from './SummaryTableTitle';

type OutstandingClientQueriesProps = {
    status: ClientQueryStatusEnum.ACTIVE | ClientQueryStatusEnum.DRAFT;
};

function OutstandingClientQueries({ status }: OutstandingClientQueriesProps): JSX.Element {
    const { id } = useWorkpaperContext();
    const clientQueries = useClientQueries(id, {
        filter: `status=${status}`,
        search: '',
        sort: 'sort[dir]=asc&sort[field]=createdAt',
    });

    return (
        <div>
            <div className="border-b pb-3 flex items-center justify-between">
                <SummaryTableTitle
                    title="Client Queries"
                    count={clientQueries.data ? (clientQueries.data?.data.length as number).toString() : '0'}
                    status={status === ClientQueryStatusEnum.ACTIVE ? 'Active' : 'Draft'}
                    statusBadgeColor={status === ClientQueryStatusEnum.ACTIVE ? 'yellow' : 'red'}
                />
                <Link to="../client-queries" className="text-xs font-medium text-gray-700">
                    See all
                </Link>
            </div>
            {clientQueries.isLoading && <></>}
            {clientQueries.isError && <p className="block text-center text-sm text-gray-500 pt-4">Unable to get client queries.</p>}
            {clientQueries.isSuccess && (
                <div className="max-h-[350px] overflow-auto">
                    {clientQueries.data.data.length === 0 ? (
                        <div className="mt-12">
                            <EmptyStateNew
                                icon={<InboxIcon />}
                                title={`No ${status.toLowerCase()} queries`}
                                titleSize="small"
                                helpText={`See any ${status.toLowerCase()} queries preventing certification.`}
                                helpLink="https://learn.cimplico.com/docs/dashboard-overview#common-blockers"
                            />
                        </div>
                    ) : (
                        <div className="mt-4">
                            {clientQueries.data.data.map((ic) => {
                                return <ClientQuery key={ic.id} clientQuery={ic} />;
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

type ClientQueryProps = {
    clientQuery: ClientQueryOverview;
};
function ClientQuery({ clientQuery }: ClientQueryProps): JSX.Element {
    const { firstName, lastName, profileHexColour, avatarUrl } = clientQuery.createdBy;
    const context = getQueryContext(clientQuery);

    const msInDay = 86_400_000;

    const dayDifference = Math.floor((new Date().getTime() - new Date(clientQuery.createdAt).getTime()) / msInDay);

    const dateColour = dayDifference > 7 ? 'text-red-700' : dayDifference >= 5 ? 'text-yellow-600' : 'text-gray-800';

    return (
        <Link
            className="flex items-end justify-between mt-2 gap-8 hover:bg-gray-100 p-2"
            to={`../client-queries?activeQuery=${clientQuery.id}`}
        >
            <div className="w-[70%]">
                <div className="flex items-center">
                    <UserAvatar user={{ firstName, lastName, avatarUrl, profileHexColour }} />
                    <div className="ml-2">
                        <div className="flex items-start">
                            {context ? (
                                <Link to={context.url} className="text-xs text-indigo-600 hover:underline">
                                    {context.label}
                                </Link>
                            ) : (
                                <p className="text-xs text-gray-600">-</p>
                            )}
                        </div>
                        <p className="text-sm text-gray-800 line-clamp-1">{clientQuery.rawQuery}</p>
                    </div>
                </div>
            </div>
            <div>
                <TooltipV2 message={formatDateTime(clientQuery.createdAt)} position="left">
                    <p className={`${dateColour} text-sm`}>{calcRelativeTime(clientQuery.createdAt, true)}</p>
                </TooltipV2>
            </div>
        </Link>
    );
}

export default OutstandingClientQueries;
